import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section } from "@/components/site/Blocks";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ADMIN_PDF_LIBRARY } from "@/lib/admin-pdf-library";

export const Route = createFileRoute("/lo/documents")({
  ssr: false,
  head: () =>
    buildHead({
      title: "Loan Officer Document Library | Internal Tools",
      description: "Internal loan officer tool for downloading sample branded DSCR and short-term rental PDFs used across the borrower experience.",
      url: "/lo/documents",
      noindex: true,
      localBusiness: false,
    }),
  component: DocumentLibraryPage,
});

function DocumentLibraryPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!active) return;
      if (!data.user) {
        navigate({ to: "/auth", search: { redirect: "/lo/documents" }, replace: true });
        return;
      }
      setChecking(false);
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleDownload(id: string, generate: () => Promise<void>) {
    setBusy(id);
    try {
      await generate();
    } catch {
      toast.error("Could not build that document. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleDownloadAll() {
    setBusy("all");
    try {
      for (const doc of ADMIN_PDF_LIBRARY) {
        await doc.generate();
        await new Promise((r) => setTimeout(r, 400));
      }
      toast.success("All documents downloaded.");
    } catch {
      toast.error("Some documents failed to build.");
    } finally {
      setBusy(null);
    }
  }

  if (checking) {
    return (
      <SiteShell>
        <Section>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your access…
          </div>
        </Section>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <Section>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-brand">
              <ShieldCheck className="h-3.5 w-3.5" /> Internal — loan officer tools
            </div>
            <h1 className="mt-2 text-3xl font-black tracking-tight">Document library</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Every branded DSCR and short-term rental PDF the site produces. Each download is a
              sample copy with placeholder figures — same logo, palette, and disclosure language a
              borrower receives — so you can review wording before it goes out.
            </p>
          </div>
          <Button onClick={handleDownloadAll} disabled={busy !== null}>
            {busy === "all" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building…
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" /> Download all
              </>
            )}
          </Button>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {ADMIN_PDF_LIBRARY.map((doc) => (
            <article
              key={doc.id}
              className="flex flex-col rounded-2xl border border-border bg-card p-5"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-brand/10 p-2 text-brand">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {doc.category}
                  </div>
                  <h2 className="text-base font-bold leading-snug">{doc.title}</h2>
                </div>
              </div>
              <p className="mt-3 flex-1 text-sm text-muted-foreground">{doc.description}</p>
              <div className="mt-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Where borrowers get it:</span>{" "}
                {doc.usedOn}
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  disabled={busy !== null}
                  onClick={() => handleDownload(doc.id, doc.generate)}
                >
                  {busy === doc.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building…
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" /> Download PDF
                    </>
                  )}
                </Button>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Sample figures are illustrative placeholders. Live borrower copies are generated from the
          borrower&apos;s own application, market data, and property inputs.
        </p>
      </Section>
    </SiteShell>
  );
}
